import { ArtifactChoice, MultipleChoiceArtifact } from '@dreamai/types';
import { formatMoney } from './hotelArtifact';

/**
 * Flight payload carried by choices of a `flights` multiple-choice artifact.
 *
 * Mirrors `FlightOffer` & friends in dream-types (`api/response-artifacts.ts`,
 * dream-types#76). Switch to `import from '@dreamai/types'` once that version
 * is published and bumped here.
 */

/**
 * `instant`: a complete itinerary whose exact fares Dream books itself.
 * `select`: an outbound from a round-trip search — pick it to see returns.
 * `agent`: booked directly with the airline by the assistant.
 */
export type FlightBookingMode = 'instant' | 'select' | 'agent';

export interface FlightOfferSegment {
  carrier: string;
  carrierName: string;
  flightNumber: string;
  operatedBy?: string | null;
  origin: string;
  destination: string;
  originTerminal?: string | null;
  destinationTerminal?: string | null;
  /** Local time, ISO-8601 without offset: "2026-10-02T18:50". */
  departingAt: string;
  arrivingAt: string;
  durationMinutes?: number | null;
  aircraft?: string | null;
}

export interface FlightOfferSlice {
  origin: string;
  destination: string;
  departingAt: string;
  arrivingAt: string;
  durationMinutes?: number | null;
  stops: number;
  segments: FlightOfferSegment[];
}

export interface FlightOfferFare {
  offerId: string;
  brandName: string;
  cabin: string;
  total: number;
  perPassenger: number;
  currency: string;
  checkedBags?: number | null;
  carryOnBags?: number | null;
  refundable: boolean | null;
  changeable: boolean | null;
  conditions: string;
  requiresPassport: boolean;
  expiresAt: string;
}

export interface FlightOfferSource {
  name: string;
  total?: number | null;
  currency: string;
  official?: boolean;
  url?: string | null;
}

export interface FlightOffer {
  bookingMode: FlightBookingMode;
  slices: FlightOfferSlice[];
  passengerCount: number;
  returnDate?: string | null;
  departureTokenId?: string | null;
  fares?: FlightOfferFare[];
  returnOptions?: number | null;
  sources?: FlightOfferSource[];
  cheapestOfferId?: string | null;
  hasRefundableFare: boolean;
  /** Two one-way tickets; `sources` holds the outbound then the return fare. */
  separateTickets?: boolean;
  /** True on every row of a round-trip search whose returns come paired. */
  pairedSearch?: boolean;
  /** Paired Dream row: how many other returns exist for its outbound. */
  returnAlternatives?: number | null;
  /**
   * Which leg this row is: a one-way flight, the outbound of a return trip,
   * or the return leg. Older cards may not have it; then legs are labelled
   * by their position in the list.
   */
  stage?: 'one_way' | 'outbound' | 'return';
}

export type FlightChoice = ArtifactChoice & {
  choiceBlurb?: string;
  fromPrice?: number;
  flightOffer?: FlightOffer;
  returnFor?: string;
};

export type FlightPassengerTitle = 'mr' | 'ms' | 'mrs' | 'miss' | 'dr';
export type FlightPassengerGender = 'm' | 'f';

export interface FlightPassengerPassport {
  number: string;
  /** YYYY-MM-DD */
  expiresOn: string;
  /** ISO 3166-1 alpha-2 */
  issuingCountry: string;
}

/** Who is flying — sent with the Book press, editable on the confirm sheet. */
export interface FlightBookingPassengerDetails {
  title: FlightPassengerTitle;
  givenName: string;
  familyName: string;
  gender: FlightPassengerGender;
  /** YYYY-MM-DD */
  dateOfBirth: string;
  email: string;
  phone: string;
  passport?: FlightPassengerPassport | null;
}

/** What the artifact endpoints know about the traveller(s) up front. */
export type FlightPassengerPrefill = Partial<FlightBookingPassengerDetails>;

/** The confirm sheet's editable copy: every field a string, nothing inferred. */
export interface FlightPassengerDraft {
  title: FlightPassengerTitle | '';
  givenName: string;
  familyName: string;
  gender: FlightPassengerGender | '';
  dateOfBirth: string;
  email: string;
  phone: string;
  passportNumber: string;
  passportExpiresOn: string;
  passportIssuingCountry: string;
}

export type FlightNotBookedReason =
  | 'price_changed'
  | 'terms_changed'
  | 'offer_expired'
  | 'already_booked'
  | 'missing_passenger_details'
  | 'invalid_passenger_details'
  | 'insufficient_balance'
  | 'declined'
  | 'rejected';

/**
 * Progress and outcome of the booking the system runs for a Book press.
 * Written by the backend onto `userResponse.flightBooking` and pushed as
 * `RESPONSE_ARTIFACT_UPDATED`.
 */
export type FlightArtifactBookingState =
  | { state: 'booking'; startedAt: string }
  | {
      /**
       * Dream is taking the exact fare from the saved card; books
       * automatically once paid. `heldBookingReference` is set when the
       * airline is holding the seats and price meanwhile.
       */
      state: 'awaiting_payment';
      bookingId: string;
      paymentId: string;
      last4: string | null;
      total: number;
      currency: string;
      heldBookingReference: string | null;
      updatedAt: string;
    }
  | {
      state: 'booked';
      /** `pending` = order placed, ticketing awaited — never shown as confirmed. */
      status: 'confirmed' | 'pending';
      bookingId: string;
      bookingReference: string | null;
      ticketNumbers: string[];
      airlineNames: string[];
      slices: FlightOfferSlice[];
      passengers: { givenName: string; familyName: string }[];
      fareBrand: string;
      total: number;
      currency: string;
      conditions: string;
      updatedAt: string;
    }
  | {
      state: 'not_booked';
      reason: FlightNotBookedReason;
      current?: { total: number; currency: string; conditions: string };
      detail?: string;
      updatedAt: string;
    }
  | { state: 'unknown'; bookingId: string | null; updatedAt: string };

export type FlightArtifactUserResponse = NonNullable<
  MultipleChoiceArtifact['userResponse']
> & {
  selectedOfferId?: string;
  passengers?: FlightBookingPassengerDetails[];
  flightBooking?: FlightArtifactBookingState;
};

/** The selection the client sends back when the user presses Book / Select. */
export interface FlightBookingSelection {
  choiceId: string;
  /** Exact fare the user pressed Book on (instant itineraries only). */
  offerId?: string;
  /** Passengers as confirmed on the sheet (instant itineraries only). */
  passengers?: FlightBookingPassengerDetails[];
}

const FLIGHTS_VIEW = 'flights';

export function isFlightsView(view: string | undefined): boolean {
  return view === FLIGHTS_VIEW;
}

export function asFlightChoice(choice: ArtifactChoice): FlightChoice {
  return choice;
}

export function getFlightOffer(
  choice: ArtifactChoice,
): FlightOffer | undefined {
  return asFlightChoice(choice).flightOffer;
}

export function getFlightFromPrice(choice: ArtifactChoice): number | undefined {
  return asFlightChoice(choice).fromPrice;
}

export function getSelectedFareId(
  artifact: MultipleChoiceArtifact,
): string | undefined {
  const response = artifact.userResponse as
    | FlightArtifactUserResponse
    | undefined;
  return response?.selectedOfferId;
}

export function getStoredPassengers(
  artifact: MultipleChoiceArtifact,
): FlightBookingPassengerDetails[] | undefined {
  const response = artifact.userResponse as
    | FlightArtifactUserResponse
    | undefined;
  return response?.passengers;
}

export function readFlightBookingState(
  userResponse: unknown,
): FlightArtifactBookingState | null {
  let parsed: unknown = userResponse;
  if (typeof userResponse === 'string') {
    try {
      parsed = JSON.parse(userResponse);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const state = (parsed as FlightArtifactUserResponse).flightBooking;
  return state && typeof state === 'object' && 'state' in state ? state : null;
}

/** The press is still being worked on server-side; the outcome is yet to arrive. */
export function isInFlightFlightBookingState(
  state: FlightArtifactBookingState | null,
): boolean {
  return state?.state === 'booking' || state?.state === 'awaiting_payment';
}

export function isSettledFlightBookingState(
  state: FlightArtifactBookingState | null,
): boolean {
  return state != null && !isInFlightFlightBookingState(state);
}

const INTERNAL_DETAIL =
  /\b(duffel|offer ?id|bookflight|api|http|status \d{3}|\/passengers\/|do not|call .* again)\b/i;

/**
 * The one plain sentence the backend attaches for the user, or null when the
 * stored detail is missing or reads like agent/tool guidance.
 */
export function userFacingDetail(
  state: Extract<FlightArtifactBookingState, { state: 'not_booked' }>,
): string | null {
  const detail = state.detail?.trim();
  if (!detail || detail.length > 240 || INTERNAL_DETAIL.test(detail)) {
    return null;
  }
  return detail;
}

/** One line on why the previous Book press did not book, for the fare banner. */
export function flightNotBookedSummary(
  state: Extract<FlightArtifactBookingState, { state: 'not_booked' }>,
): string {
  switch (state.reason) {
    case 'price_changed':
      return state.current
        ? `The price changed before it could be booked — this fare is now ${formatMoney(state.current.total, state.current.currency)}.`
        : 'The price changed before it could be booked.';
    case 'terms_changed':
      return 'The fare conditions changed before it could be booked.';
    case 'offer_expired':
      return 'That fare is no longer available.';
    case 'already_booked':
      return 'That fare was already booked.';
    case 'missing_passenger_details':
      return 'Passenger details were missing.';
    case 'invalid_passenger_details':
      return (
        userFacingDetail(state) ??
        'The airline could not accept the passenger details.'
      );
    case 'insufficient_balance':
      return 'There was not enough balance to book it.';
    default:
      return (
        userFacingDetail(state) ?? "The airline couldn't take that booking."
      );
  }
}

export function findFare(
  offer: FlightOffer | undefined,
  offerId: string | undefined,
): FlightOfferFare | null {
  if (!offer || !offerId) return null;
  return offer.fares?.find((f) => f.offerId === offerId) ?? null;
}

// ─── Passenger drafts ────────────────────────────────────────────────────────

export const EMPTY_PASSENGER_DRAFT: FlightPassengerDraft = {
  title: '',
  givenName: '',
  familyName: '',
  gender: '',
  dateOfBirth: '',
  email: '',
  phone: '',
  passportNumber: '',
  passportExpiresOn: '',
  passportIssuingCountry: '',
};

export function draftFromPassenger(
  passenger: FlightPassengerPrefill | undefined,
): FlightPassengerDraft {
  if (!passenger) return EMPTY_PASSENGER_DRAFT;
  return {
    title: passenger.title ?? '',
    givenName: passenger.givenName ?? '',
    familyName: passenger.familyName ?? '',
    gender: passenger.gender ?? '',
    dateOfBirth: passenger.dateOfBirth ?? '',
    email: passenger.email ?? '',
    phone: passenger.phone ?? '',
    passportNumber: passenger.passport?.number ?? '',
    passportExpiresOn: passenger.passport?.expiresOn ?? '',
    passportIssuingCountry: passenger.passport?.issuingCountry ?? '',
  };
}

/** Fills the blanks of `current` from `prefill`; anything already typed wins. */
export function mergePassengerDraft(
  current: FlightPassengerDraft,
  prefill: FlightPassengerPrefill | undefined,
): FlightPassengerDraft {
  const fromPrefill = draftFromPassenger(prefill);
  const merged = { ...current };
  for (const key of Object.keys(merged) as Array<keyof FlightPassengerDraft>) {
    if (!merged[key]) {
      (merged as Record<keyof FlightPassengerDraft, string>)[key] =
        fromPrefill[key];
    }
  }
  return merged;
}

/** `count` drafts: previously confirmed passengers first, then prefill, then blanks. */
export function initialPassengerDrafts(
  count: number,
  stored: FlightPassengerPrefill[] | undefined,
): FlightPassengerDraft[] {
  return Array.from({ length: Math.max(1, count) }, (_, i) =>
    draftFromPassenger(stored?.[i]),
  );
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[\d\s().-]{6,}$/;
const COUNTRY = /^[A-Za-z]{2}$/;

function isTitle(value: string): value is FlightPassengerTitle {
  return (
    value === 'mr' ||
    value === 'ms' ||
    value === 'mrs' ||
    value === 'miss' ||
    value === 'dr'
  );
}

function isGender(value: string): value is FlightPassengerGender {
  return value === 'm' || value === 'f';
}

function validDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d
  );
}

/** Field keys that still need attention before the fare can be booked. */
export function passengerDraftIssues(
  draft: FlightPassengerDraft,
  requiresPassport: boolean,
): Array<keyof FlightPassengerDraft> {
  const issues: Array<keyof FlightPassengerDraft> = [];
  if (!isTitle(draft.title)) issues.push('title');
  if (draft.givenName.trim().length === 0) issues.push('givenName');
  if (draft.familyName.trim().length === 0) issues.push('familyName');
  if (!isGender(draft.gender)) issues.push('gender');
  if (!validDate(draft.dateOfBirth.trim())) issues.push('dateOfBirth');
  if (!EMAIL.test(draft.email.trim())) issues.push('email');
  if (!PHONE.test(draft.phone.trim())) issues.push('phone');
  const passportStarted =
    draft.passportNumber.trim() ||
    draft.passportExpiresOn.trim() ||
    draft.passportIssuingCountry.trim();
  if (requiresPassport || passportStarted) {
    if (draft.passportNumber.trim().length === 0) issues.push('passportNumber');
    if (!validDate(draft.passportExpiresOn.trim()))
      issues.push('passportExpiresOn');
    if (!COUNTRY.test(draft.passportIssuingCountry.trim()))
      issues.push('passportIssuingCountry');
  }
  return issues;
}

export function passengersComplete(
  drafts: FlightPassengerDraft[],
  requiresPassport: boolean,
): boolean {
  return (
    drafts.length > 0 &&
    drafts.every((d) => passengerDraftIssues(d, requiresPassport).length === 0)
  );
}

/** Drafts → the exact shape the backend books; call only once complete. */
export function passengersFromDrafts(
  drafts: FlightPassengerDraft[],
): FlightBookingPassengerDetails[] {
  return drafts.map((d) => {
    const passportNumber = d.passportNumber.trim();
    return {
      title: isTitle(d.title) ? d.title : 'mr',
      givenName: d.givenName.trim(),
      familyName: d.familyName.trim(),
      gender: isGender(d.gender) ? d.gender : 'm',
      dateOfBirth: d.dateOfBirth.trim(),
      email: d.email.trim(),
      phone: d.phone.trim(),
      passport: passportNumber
        ? {
            number: passportNumber,
            expiresOn: d.passportExpiresOn.trim(),
            issuingCountry: d.passportIssuingCountry.trim().toUpperCase(),
          }
        : null,
    };
  });
}

export function passengerName(p: {
  givenName: string;
  familyName: string;
}): string {
  return `${p.givenName} ${p.familyName}`.trim();
}

// ─── Presentation ────────────────────────────────────────────────────────────

/** "2026-10-02T18:50" → "18:50" (local time as the airline states it). */
export function formatClock(localIso: string): string {
  const match = /T(\d{2}:\d{2})/.exec(localIso);
  return match ? match[1] : localIso;
}

/** "2026-10-02T18:50" → "Fri 2 Oct" (calendar date, no timezone shift). */
export function formatFlightDate(localIso: string): string {
  const [y, m, d] = localIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return localIso;
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes == null || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

/** Days the arrival is after the departure date; 0 when same day. */
export function overnightDays(slice: FlightOfferSlice): number {
  const dep = slice.departingAt.slice(0, 10);
  const arr = slice.arrivingAt.slice(0, 10);
  if (!dep || !arr || dep === arr) return 0;
  const [dy, dm, dd] = dep.split('-').map(Number);
  const [ay, am, ad] = arr.split('-').map(Number);
  const diff =
    (Date.UTC(ay, am - 1, ad) - Date.UTC(dy, dm - 1, dd)) / 86_400_000;
  return Number.isFinite(diff) && diff > 0 ? Math.round(diff) : 0;
}

export function stopsLabel(slice: FlightOfferSlice): string {
  if (slice.stops === 0) return 'Direct';
  const via = slice.segments
    .slice(0, -1)
    .map((s) => s.destination)
    .join(', ');
  return `${slice.stops} stop${slice.stops === 1 ? '' : 's'}${via ? ` via ${via}` : ''}`;
}

/** Distinct marketing carriers on a slice, e.g. "British Airways" or "KLM · Air France". */
export function carriersLabel(slices: FlightOfferSlice[]): string {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const slice of slices) {
    for (const seg of slice.segments) {
      const name = seg.carrierName || seg.carrier;
      if (!seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    }
  }
  return names.join(' · ');
}

export function flightNumbersLabel(slice: FlightOfferSlice): string {
  return slice.segments.map((s) => `${s.carrier}${s.flightNumber}`).join(', ');
}

export function bagsLabel(fare: FlightOfferFare): string | null {
  const parts: string[] = [];
  if (fare.carryOnBags != null) {
    parts.push(
      fare.carryOnBags === 0
        ? 'No cabin bag'
        : `${fare.carryOnBags} cabin bag${fare.carryOnBags === 1 ? '' : 's'}`,
    );
  }
  if (fare.checkedBags != null) {
    parts.push(
      fare.checkedBags === 0
        ? 'No checked bag'
        : `${fare.checkedBags} checked bag${fare.checkedBags === 1 ? '' : 's'}`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function passengersLabel(count: number): string {
  return `${count} passenger${count === 1 ? '' : 's'}`;
}

export function tripLabel(offer: FlightOffer): string {
  const first = offer.slices[0];
  if (!first) return '';
  if (offer.slices.length > 1) {
    return `${first.origin} ⇄ ${first.destination}`;
  }
  return `${first.origin} → ${first.destination}`;
}

export function fareLabel(fare: FlightOfferFare): string {
  return fare.cabin && !/^economy$/i.test(fare.cabin)
    ? `${fare.brandName} · ${fare.cabin}`
    : fare.brandName;
}

export { formatMoney };
